import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, SelectGroup } from '@aqarmap/aqarmap-ui';
import useFilterByDate from '~myAccount/pages/Wallet/hooks/useFilterByDate';
import {
  selectedValueType,
  SelectedDateType,
  selectedRangeType,
} from '~myAccount/pages/Wallet/types';

type DateFilterDropdownProps = {
  setSelectedDate: (value: SelectedDateType) => void;
  onSubmit: (range: selectedRangeType) => void;
  selectedDate: SelectedDateType;
};

export default function DateFilterDropdown({
  setSelectedDate,
  selectedDate,
  onSubmit,
}: DateFilterDropdownProps) {
  const { t } = useTranslation('app');
  const { options: dateOptions, dateRangeValues } = useFilterByDate();
  const [showCustomDate, setShowCustomDate] = useState<boolean>(false);
  const [customRange, setCustomRange] = useState<SelectedDateType['range']>(
    dateRangeValues['customDate'],
  );

  const handleCustomDateChange = (
    e: ChangeEvent<HTMLInputElement>,
    type: 'date_from' | 'date_to',
  ) => {
    const value = e.target.value;
    const updateDateRange = {
      ...customRange,
      [type]: value,
    };

    setCustomRange(updateDateRange);
  };

  const handleDateInputClick = (e: MouseEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    (input as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
  };

  const onSelectChange = (selectedValue: selectedValueType) => {
    const dateRange = dateRangeValues[selectedValue];
    setSelectedDate({
      value: selectedValue,
      range: dateRange,
      isCustom: selectedValue === 'customDate',
    });
    setShowCustomDate(selectedValue === 'customDate');
    if (selectedValue !== 'customDate') {
      onSubmit(dateRange);
    }
  };

  const isChecked = useCallback(
    (value: string | number | readonly string[] | undefined) => {
      if (selectedDate.isCustom) return value === 'customDate';
      return value === selectedDate.value;
    },
    [selectedDate],
  );

  const controlledDateOptions = useMemo(
    () =>
      dateOptions.map((option) => ({
        ...option,
        field: {
          ...option.field,
          checked: isChecked(option.field.value),
        },
      })),
    [dateOptions, isChecked],
  );

  useEffect(() => {
    setShowCustomDate(selectedDate.isCustom ?? false);
    setCustomRange(selectedDate.range);
  }, [selectedDate]);

  return (
    <div className="flex flex-col gap-x date-list md:py-2x w-full">
      <SelectGroup
        list={controlledDateOptions}
        size="medium"
        className=""
        handleChange={(_e, selectedValue: selectedValueType) => {
          onSelectChange(selectedValue);
        }}
        name="date"
        label={{ text: t('date'), className: 'md:hidden text-link' }}
      />
      <div className="flex flex-col gap-3x md:gap-2x px-2x w-full ">
        {showCustomDate ? (
          <div className="flex flex-col gap-3x md:gap-2x">
            <div className="grid grid-cols-2 gap-1.5x">
              <Input
                field={{
                  type: 'date',
                  name: 'fromDate',
                  placeholder: 'yyyy-mm-dd',
                  value: customRange?.date_from,
                  onClick: handleDateInputClick,
                  onChange: (e) => {
                    handleCustomDateChange(e, 'date_from');
                  },
                }}
                label={{
                  text: t('from'),
                  className: 'text-body_1 text-gray__dark_2',
                }}
              />
              <Input
                field={{
                  type: 'date',
                  name: 'toDate',
                  placeholder: 'yyyy-mm-dd',
                  value: customRange?.date_to,
                  onClick: handleDateInputClick,
                  onChange: (e) => {
                    handleCustomDateChange(e, 'date_to');
                  },
                }}
                label={{
                  text: t('to'),
                  className: 'text-body_1 text-gray__dark_2',
                }}
              />
            </div>
            <Button
              variant="primary"
              size="medium"
              className="w-full "
              onClick={() => {
                setSelectedDate({
                  value: `${t('from')} ${customRange.date_from} ${t('to')} ${customRange.date_to}`,
                  range: customRange,
                  isCustom: true,
                });
                onSubmit(customRange);
              }}
            >
              {t('save')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
